import { Icons, useToggle } from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { SERVICE_REQUEST_CATALOG_TO_EMAIL_MAP } from '../../email-map';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';
import { ServiceRequestTaskButton } from '../drawer/ServiceRequestTaskButton';
import { ServiceRequestTaskLabel } from '../labels';
import { SendEmailModal } from '../modals/SendEmailModal';
import { ServiceRequestTaskIconConfig } from '../taskIconConfig';

export const ServiceRequestSendEmailCardTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const sendEmailModalVisibility = useToggle();

  const stateMap = {
    [TaskParty.CLIENT]: {
      [TaskStatus.TO_DO]: {
        actions: null,
        label: <ServiceRequestTaskLabel label="Receive an email update" />,
      },
      [TaskStatus.BACKLOG]: {
        actions: null,
        label: <ServiceRequestTaskLabel label="Receive an email update" />,
      },
      [TaskStatus.COMPLETED]: {
        actions: null,
        label: (
          <ServiceRequestTaskLabel
            label="Received an email update"
            timestamp={task.updatedAt}
            status={task.status}
          />
        ),
      },
    },
    [TaskParty.AGENT]: {
      [TaskStatus.TO_DO]: {
        actions: (
          <ServiceRequestTaskButton onClick={sendEmailModalVisibility.show}>
            <Icons.Mail01 className="w-4" />
            Draft Mail
          </ServiceRequestTaskButton>
        ),
        label: <span>Send email update</span>,
      },
      [TaskStatus.BACKLOG]: {
        actions: (
          <ServiceRequestTaskButton disabled>
            <Icons.Mail01 className="w-4" />
            Draft Mail
          </ServiceRequestTaskButton>
        ),
        label: <ServiceRequestTaskLabel label="Send email update" />,
      },
      [TaskStatus.COMPLETED]: {
        actions: null,
        label: (
          <ServiceRequestTaskLabel
            label="Email update sent"
            timestamp={task.updatedAt}
            status={task.status}
          />
        ),
      },
    },
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      return (
        // Admin viewers run these steps too, and an unmapped role renders no actions.
        stateMap[state.role === TaskParty.ADMIN ? TaskParty.AGENT : state.role]?.[
          state.status
        ] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  const serviceId = getServiceRequestServiceId(serviceRequest);
  const currentServiceCatalog = serviceId
    ? SERVICE_REQUEST_CATALOG_TO_EMAIL_MAP[serviceId]
    : undefined;

  const emailTemplate =
    currentServiceCatalog?.[task?.taskGroup?.legacyServiceRequestTaskGroupTaskGroupId];

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        context={context}
        config={config}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <SendEmailModal
        isOpen={sendEmailModalVisibility.visible}
        setIsOpen={sendEmailModalVisibility.hide}
        config={{
          subHeading:
            'This email lets your clients know about the progress you made on this request.',
          subject: emailTemplate?.subject,
          body: emailTemplate?.body,
          onSuccess: async () => {
            await updateTaskStatus({
              task,
              status: TaskStatus.COMPLETED,
              mutate,
            });
          },
        }}
        serviceRequest={serviceRequest}
      />
    </div>
  );
};
